/**
 * Canvas scaler: owns the logical↔physical mapping (the engine renders 1:1 to canvas backing
 * pixels and has no DPR concept — see design §Rendering). Layout runs in LOGICAL units;
 * paint + input multiply by `scale` and work in PHYSICAL pixels.
 */
export type UIScaleMode = 'constant-pixel' | 'scale-with-height';

export interface UIScalerConfig {
  mode: UIScaleMode;
  /** For 'scale-with-height': the logical viewport height (default 1080). */
  referenceHeight: number;
}

export const DEFAULT_SCALER: UIScalerConfig = { mode: 'scale-with-height', referenceHeight: 1080 };

export interface UIViewportMapping {
  /** logical → physical multiplier */
  scale: number;
  logicalWidth: number;
  logicalHeight: number;
}

export function computeViewportMapping(
  physicalWidth: number,
  physicalHeight: number,
  config: UIScalerConfig = DEFAULT_SCALER,
): UIViewportMapping {
  const scale =
    config.mode === 'scale-with-height' && config.referenceHeight > 0
      ? physicalHeight / config.referenceHeight
      : 1;
  const s = scale > 0 ? scale : 1;
  return { scale: s, logicalWidth: physicalWidth / s, logicalHeight: physicalHeight / s };
}

/** Screen-space ortho: physical pixels (origin top-left, y-down) → NDC. Column-major (engine convention). */
export function screenOrtho(out: Float32Array, width: number, height: number): Float32Array {
  out.fill(0);
  out[0] = 2 / width;
  out[5] = -2 / height;
  out[10] = 1;
  out[12] = -1;
  out[13] = 1;
  out[15] = 1;
  return out;
}
